import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  useTheme,
  Avatar,
  Divider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Header from "components/Header";
import {
  ExpandMoreOutlined,
  AddCircleOutline,
  RefreshOutlined,
  CancelOutlined,
  CalculateOutlined,
  WorkHistory,
  AccountBalanceWallet,
  Savings,
  MoneyOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchListKaryawanNotListGaji,
  fetchSummaryAbsensi,
  resetSummary,
} from "state/karyawanActions";
import { getNamaBulan } from "utils/namaBulan";
import FlexStart from "components/FlexStart";
import { fetchAddListGaji } from "state/gajiAction";
import { setMessage, setStatus } from "state";
import FloatingAlert from "components/FloatingAlert";

import moment from "moment";
import "moment/locale/id";
moment.locale("id");

// Schema validasi pakai Yup
const validationSchema = Yup.object({
  id_karyawan: Yup.mixed().required("ID Karyawan wajib diisi"),
  gaji_pokok: Yup.number().required("Gaji Pokok wajib diisi"),
  lembur: Yup.number(),
  rajin: Yup.number(),
  transportasi: Yup.number(),
  pulsa: Yup.number(),
  tunjangan_jabatan: Yup.number(),
  uang_makan: Yup.number(),
  potongan_tabungan: Yup.number(),
  potongan_pinjaman: Yup.number(),
});

const AddPenggajian = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const { bulan, tahun } = location.state || {};
  const { list_karyawan_belum_gaji, summary_absensi } = useSelector(
    (state) => state.karyawan,
  );

  const { status, message } = useSelector((state) => state.global);
  const [baseGajiPokok, setBaseGajiPokok] = useState(0);
  const [baseTotalHadir, setBaseTotalHadir] = useState(0);

  useEffect(() => {
    dispatch(fetchListKaryawanNotListGaji(bulan, tahun));
  }, []);

  const handleCloseSnackbar = () => {
    if (status === "success") {
      formik.resetForm();
      dispatch(fetchListKaryawanNotListGaji(bulan, tahun));
      dispatch(resetSummary());
    }
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
  };

  const handleSubmitGaji = (value) => {
    dispatch(fetchAddListGaji(value));
  };

  const formik = useFormik({
    initialValues: {
      id_karyawan: null,
      gaji_pokok: 0,
      lembur: 0,
      rajin: 0,
      transportasi: 0,
      pulsa: 0,
      tunjangan_jabatan: 0,
      uang_makan: 0,
      potongan_nbh: 0,
      sisa_nbh_bln_ini: 0,
      nbh_bln_ini: 0,
      potongan_tabungan: 0,
      potongan_pinjaman: 0,
    },
    validationSchema,
    onSubmit: (values) => {
      const reqData = {
        id_karyawan: values.id_karyawan,
        bulan: bulan,
        tahun: tahun,
        gaji_pokok: values.gaji_pokok,
        lembur: values.lembur,
        rajin: values.rajin,
        transportasi: values.transportasi,
        pulsa: values.pulsa,
        tunjangan_jabatan: values.tunjangan_jabatan,
        uang_makan: values.uang_makan,
        potongan_nbh: values.potongan_nbh,
        sisa_nbh_bln_ini: values.sisa_nbh_bln_ini,
        nbh_bln_ini: values.nbh_bln_ini,
        sisa_nbh: hitungSisaNBH(),
        potongan_tabungan: values.potongan_tabungan,
        potongan_pinjaman: values.potongan_pinjaman,
        total_gaji: hitungTotalGaji(),
      };
      handleSubmitGaji(reqData);
    },
  });

  useEffect(() => {
    if (formik.values.id_karyawan) {
      dispatch(fetchSummaryAbsensi(formik.values.id_karyawan, bulan, tahun));
    } else {
      dispatch(resetSummary());
    }
  }, [formik.values.id_karyawan]);

  useEffect(() => {
    if (summary_absensi) {
      kalkulasiGajiKaryawan();
    }
  }, [summary_absensi]);

  const hitungIncome = () => {
    const {
      gaji_pokok = 0,
      lembur = 0,
      rajin = 0,
      transportasi = 0,
      pulsa = 0,
      tunjangan_jabatan = 0,
      uang_makan = 0,
    } = formik.values;

    const totalPendapatan =
      Number(gaji_pokok) +
      Number(lembur) +
      Number(rajin) +
      Number(transportasi) +
      Number(pulsa) +
      Number(tunjangan_jabatan) +
      Number(uang_makan);
    return totalPendapatan;
  };

  const hitungOutcome = () => {
    const {
      potongan_nbh = 0,
      potongan_tabungan = 0,
      potongan_pinjaman = 0,
    } = formik.values;

    const totalPotongan =
      Number(potongan_nbh) +
      Number(potongan_tabungan) +
      Number(potongan_pinjaman);

    return totalPotongan;
  };

  const hitungTotalGaji = () => {
    const {
      gaji_pokok = 0,
      lembur = 0,
      rajin = 0,
      transportasi = 0,
      pulsa = 0,
      tunjangan_jabatan = 0,
      uang_makan = 0,
      potongan_nbh = 0,
      potongan_tabungan = 0,
      potongan_pinjaman = 0,
    } = formik.values;

    const totalPendapatan =
      Number(gaji_pokok) +
      Number(lembur) +
      Number(rajin) +
      Number(transportasi) +
      Number(pulsa) +
      Number(tunjangan_jabatan) +
      Number(uang_makan);

    const totalPotongan =
      Number(potongan_nbh) +
      Number(potongan_tabungan) +
      Number(potongan_pinjaman);

    return totalPendapatan - totalPotongan;
  };

  const formatRupiah = (amount) => {
    if (amount === null || amount === undefined) return "Rp 0";

    const absoluteAmount = Math.abs(amount);

    const formatted = absoluteAmount.toLocaleString("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return `Rp ${formatted}`;
  };

  const hitungSisaNBH = () => {
    const {
      potongan_nbh = 0,
      sisa_nbh_bln_ini = 0,
      nbh_bln_ini = 0,
    } = formik.values;

    const sisa_nbh_skrg =
      Number(sisa_nbh_bln_ini) + Number(nbh_bln_ini) - Number(potongan_nbh);

    return sisa_nbh_skrg;
  };

  const hitungEstimasiTabungan = () => {
    const { potongan_tabungan = 0 } = formik.values;

    const est_tabungan =
      Number(potongan_tabungan) + Number(summary_absensi?.saldo_tabungan || 0);

    return est_tabungan;
  };

  const kalkulasiGajiKaryawan = () => {
    const listRetail = ["PC 02","PC 03", "PC 04", "PC 05", "PC 07", "PC 11"];
    const listSemiGrosir = ["PC 08", "PC 09", "PC 10", "PC 12"];
    const listGrosir = ["PC 01", "PC 06", "PS 01"];

    const GAJI_DATA = {
      Retail: {
        SMA: {
          "Kepala Toko(KT)": {
            gaji_pokok: 2150000,
            transportasi: 100000,
            pulsa: 50000,
            tunjangan_jabatan: 500000,
            potongan_tabungan: 100000,
          },
          "Asisten Kepala Toko(AKT)": {
            gaji_pokok: 1869900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 300000,
            potongan_tabungan: 75000,
          },
          "Merchandiser (MD)": {
            gaji_pokok: 1585000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 200000,
            potongan_tabungan: 50000,
          },
          "Kasir": {
            gaji_pokok: 1485000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 150000,
            potongan_tabungan: 30000,
          },
          "Pramuniaga (Pramu)": {
            gaji_pokok: 1485000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 30000,
          },
          "Trainee": {
            gaji_pokok: 1477890,
            transportasi: 0,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 0,
          },
        },
        SMP: {
          "Kepala Toko(KT)": {
            gaji_pokok: 1600000,
            transportasi: 100000,
            pulsa: 50000,
            tunjangan_jabatan: 500000,
            potongan_tabungan: 100000,
          },
          "Asisten Kepala Toko(AKT)": {
            gaji_pokok: 1429900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 300000,
            potongan_tabungan: 75000,
          },
          "Merchandiser (MD)": {
            gaji_pokok: 1314900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 200000,
            potongan_tabungan: 50000,
          },
          "Kasir": {
            gaji_pokok: 935000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 150000,
            potongan_tabungan: 30000,
          },
          "Pramuniaga (Pramu)": {
            gaji_pokok: 935000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 30000,
          },
          "Trainee": {
            gaji_pokok: 927890,
            transportasi: 0,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 0,
          },
        },
      },
      "Semi Grosir": {
        SMA: {
          "Kepala Toko(KT)": {
            gaji_pokok: 2150000,
            transportasi: 100000,
            pulsa: 50000,
            tunjangan_jabatan: 600000,
            potongan_tabungan: 100000,
          },
          "Asisten Kepala Toko(AKT)": {
            gaji_pokok: 1869900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 300000,
            potongan_tabungan: 75000,
          },
          "Merchandiser (MD)": {
            gaji_pokok: 1585000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 200000,
            potongan_tabungan: 50000,
          },
          "Kasir": {
            gaji_pokok: 1485000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 150000,
            potongan_tabungan: 30000,
          },
          "Pramuniaga (Pramu)": {
            gaji_pokok: 1485000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 30000,
          },
          "Trainee": {
            gaji_pokok: 1477890,
            transportasi: 0,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 0,
          },
        },
        SMP: {
          "Kepala Toko(KT)": {
            gaji_pokok: 1600000,
            transportasi: 100000,
            pulsa: 50000,
            tunjangan_jabatan: 500000,
            potongan_tabungan: 100000,
          },
          "Asisten Kepala Toko(AKT)": {
            gaji_pokok: 1429900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 300000,
            potongan_tabungan: 75000,
          },
          "Merchandiser (MD)": {
            gaji_pokok: 1314900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 200000,
            potongan_tabungan: 50000,
          },
          "Kasir": {
            gaji_pokok: 935000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 150000,
            potongan_tabungan: 30000,
          },
          "Pramuniaga (Pramu)": {
            gaji_pokok: 935000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 30000,
          },
          "Trainee": {
            gaji_pokok: 927890,
            transportasi: 0,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 0,
          },
        },
      },
      Grosir: {
        SMA: {
          "Kepala Toko(KT)": {
            gaji_pokok: 2150000,
            transportasi: 100000,
            pulsa: 50000,
            tunjangan_jabatan: 750000,
            potongan_tabungan: 100000,
          },
          "Asisten Kepala Toko(AKT)": {
            gaji_pokok: 1869900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 400000,
            potongan_tabungan: 75000,
          },
          "Merchandiser (MD)": {
            gaji_pokok: 1585000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 240000,
            potongan_tabungan: 50000,
          },
          "Kasir": {
            gaji_pokok: 1485000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 150000,
            potongan_tabungan: 30000,
          },
          "Pramuniaga (Pramu)": {
            gaji_pokok: 1485000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 30000,
          },
          "Trainee": {
            gaji_pokok: 1477890,
            transportasi: 0,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 0,
          },
        },
        SMP: {
          "Kepala Toko(KT)": {
            gaji_pokok: 1600000,
            transportasi: 100000,
            pulsa: 50000,
            tunjangan_jabatan: 500000,
            potongan_tabungan: 100000,
          },
          "Asisten Kepala Toko(AKT)": {
            gaji_pokok: 1429900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 300000,
            potongan_tabungan: 75000,
          },
          "Merchandiser (MD)": {
            gaji_pokok: 1314900,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 200000,
            potongan_tabungan: 50000,
          },
          "Kasir": {
            gaji_pokok: 935000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 150000,
            potongan_tabungan: 30000,
          },
          "Pramuniaga (Pramu)": {
            gaji_pokok: 935000,
            transportasi: 100000,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 30000,
          },
          "Trainee": {
            gaji_pokok: 927890,
            transportasi: 0,
            pulsa: 0,
            tunjangan_jabatan: 0,
            potongan_tabungan: 0,
          },
        },
      },
    };

    const getTipeToko = (jabatan) => {
      if (!jabatan) return null;
      if (listRetail.some((pcCode) => jabatan.includes(pcCode)))
        return "Retail";
      if (listSemiGrosir.some((pcCode) => jabatan.includes(pcCode)))
        return "Semi Grosir";
      if (listGrosir.some((pcCode) => jabatan.includes(pcCode)))
        return "Grosir";
      return null;
    };

    if (!summary_absensi) {
      return;
    }

    const namaJabatan =
      summary_absensi.data_jenjang?.[0]?.nama_jabatan ||
      summary_absensi.nama_jabatan ||
      "";
    const tipeAgunan = summary_absensi.tipe_agunan?.includes("SMA")
      ? "SMA"
      : "SMP";
    const jabatanUtama = namaJabatan ? namaJabatan.split(" - ")[0] : "";
    const tipeToko = getTipeToko(namaJabatan);

    const gajiDanTunjangan = GAJI_DATA[tipeToko]?.[tipeAgunan]?.[jabatanUtama];

    if (gajiDanTunjangan) {
      setBaseGajiPokok(gajiDanTunjangan.gaji_pokok);
      formik.setFieldValue("transportasi", gajiDanTunjangan.transportasi || 0);
      formik.setFieldValue("pulsa", gajiDanTunjangan.pulsa || 0);
      formik.setFieldValue(
        "tunjangan_jabatan",
        gajiDanTunjangan.tunjangan_jabatan || 0,
      );
      formik.setFieldValue(
        "potongan_tabungan",
        gajiDanTunjangan.potongan_tabungan || 0,
      );
    } else {
      setBaseGajiPokok(0);
      formik.setFieldValue("transportasi", 0);
      formik.setFieldValue("pulsa", 0);
      formik.setFieldValue("tunjangan_jabatan", 0);
      formik.setFieldValue("potongan_tabungan", 0);
    }

    const totalTelat = Number(summary_absensi.total_telat || 0);
    const calculateUangRajin =
      totalTelat === 0
        ? 400000
        : Math.max(0, Math.round(400000 - totalTelat * 5000));
    formik.setFieldValue("rajin", calculateUangRajin);
    formik.setFieldValue(
      "sisa_nbh_bln_ini",
      Number(summary_absensi.sisa_nbh || 0),
    );
  };

  const kalkulasiUangMakan = (value) => {
    const namaJabatan =
      summary_absensi?.data_jenjang?.[0]?.nama_jabatan ||
      summary_absensi?.nama_jabatan ||
      "";
    const jabatan = namaJabatan ? namaJabatan.split(" - ")[0] : "";

    let nominal = 0;

    if (jabatan === "Kepala Toko(KT)") {
      nominal = 455000;
    } else if (
      jabatan === "Asisten Kepala Toko(AKT)" ||
      jabatan === "Merchandiser (MD)" ||
      jabatan === "Kasir" ||
      jabatan === "Pramuniaga (Pramu)" ||
      jabatan === "Trainee"
    ) {
      nominal = 377000;
    }

    if (nominal > 0) {
      const calculateUangMakan = Math.round((value / 26) * nominal);
      formik.setFieldValue("uang_makan", calculateUangMakan);
    }
  };

  const formatFieldLabel = (field) => {
    if (field === "sisa_nbh_bln_ini") return "Sisa NBH Bulan Lalu";
    if (field === "nbh_bln_ini") return "NBH Bulan Ini";
    if (field === "potongan_nbh") return "Potongan NBH";
    return field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box p="2rem" maxWidth="1000px" mx="auto">
      <Header
        title={`Tambah Data Gaji`}
        subtitle={`${getNamaBulan(bulan)} - ${tahun}`}
      />

      <form onSubmit={formik.handleSubmit}>
        {/* Employee Selection Card */}
        <Card
          elevation={3}
          sx={{
            mt: 3,
            borderRadius: 4,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
              p: 3,
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 56,
                height: 56,
              }}
            >
              <MoneyOutlined sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Pilih Karyawan
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Pilih karyawan yang akan diinput penggajiannya
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Autocomplete
              fullWidth
              options={list_karyawan_belum_gaji}
              getOptionLabel={(option) =>
                `${option.id_karyawan} - ${option.nama_karyawan}`
              }
              value={
                list_karyawan_belum_gaji.find(
                  (item) => item.id_karyawan === formik.values.id_karyawan,
                ) || null
              }
              onChange={(event, newValue) => {
                setBaseTotalHadir(0);
                setBaseGajiPokok(0);
                dispatch(resetSummary());
                formik.resetForm();
                formik.setFieldValue(
                  "id_karyawan",
                  newValue?.id_karyawan || null,
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Karyawan"
                  name="id_karyawan"
                  error={
                    formik.touched.id_karyawan &&
                    Boolean(formik.errors.id_karyawan)
                  }
                  helperText={
                    formik.touched.id_karyawan && formik.errors.id_karyawan
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceWallet color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />

            {summary_absensi && (
              <Accordion sx={{ mt: 3, borderRadius: 2, boxShadow: 1 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreOutlined />}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <WorkHistory color="action" />
                    <Typography fontWeight="bold">Summary Pekerjaan</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Riwayat Jabatan */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Riwayat Jabatan:
                      </Typography>

                      {summary_absensi?.data_jenjang?.map((item, index) => {
                        const tanggalMulai = item.tanggal_berlaku
                          ? moment(item.tanggal_berlaku).format("DD/MM/YYYY")
                          : "-";
                        const tanggalAkhir = item.tanggal_berakhir
                          ? moment(item.tanggal_berakhir).format("DD/MM/YYYY")
                          : "Saat Ini";

                        return (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ mb: 0.5 }}
                          >
                            {`${index + 1}. ${item.nama_jabatan} (${tanggalMulai} - ${tanggalAkhir})`}
                          </Typography>
                        );
                      })}
                    </Grid>

                    {/* Informasi Detail */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Tipe Agunan:</strong>{" "}
                        {summary_absensi?.tipe_agunan}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Jabatan:</strong>{" "}
                        {summary_absensi?.nama_jabatan}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Hari Kerja:</strong>{" "}
                        {summary_absensi?.jumlah_hadir} Hari
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Hari Izin:</strong>{" "}
                        {summary_absensi?.jumlah_izin} Hari
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Total Telat:</strong>{" "}
                        {summary_absensi?.total_telat} Menit
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Tabungan:</strong>{" "}
                        {formatRupiah(summary_absensi?.saldo_tabungan)}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>

        {summary_absensi && (
          <Card
            elevation={3}
            sx={{
              mt: 3,
              borderRadius: 4,
              overflow: "hidden",
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                p: 3,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  width: 56,
                  height: 56,
                }}
              >
                <CalculateOutlined sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Form Penggajian
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Isi detail penggajian karyawan
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {/* Gaji Info */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 3,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.02)",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  mb={2}
                  fontWeight="bold"
                >
                  INFORMASI GAJI
                </Typography>
                <Typography variant="body2" mb={2}>
                  {`*Gaji Pokok perhari (${formatRupiah(baseGajiPokok)} / 26): ${formatRupiah(
                    baseGajiPokok / 26,
                  )}`}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Gaji Pokok"
                      name="gaji_pokok"
                      disabled
                      type="text"
                      value={formik.values.gaji_pokok}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyOutlined color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Total Hari Kerja"
                      name="total_hadir"
                      type="text"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      value={String(baseTotalHadir)}
                      onChange={(e) => {
                        const { value } = e.target;
                        const digits = value.replace(/\D/g, "");
                        if (Number(digits) >= 0 && Number(digits) <= 27) {
                          setBaseTotalHadir(Number(digits));
                          const calculateGaji = Math.round(
                            (baseGajiPokok / 26) * Number(digits),
                          );
                          kalkulasiUangMakan(Number(digits));
                          formik.setFieldValue("gaji_pokok", calculateGaji);
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkHistory color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Tunjangan Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  mb={2}
                  fontWeight="bold"
                >
                  TUNJANGAN & PENDAPATAN
                </Typography>
                <Grid container spacing={3}>
                  {[
                    "lembur",
                    "rajin",
                    "transportasi",
                    "pulsa",
                    "tunjangan_jabatan",
                    "uang_makan",
                  ].map((field) => (
                    <Grid item xs={12} md={6} key={field}>
                      <TextField
                        fullWidth
                        label={formatFieldLabel(field)}
                        name={field}
                        disabled={
                          formik.values.id_karyawan == null ||
                          (field !== "nbh_bln_ini" &&
                            field !== "potongan_nbh" &&
                            field !== "potongan_pinjaman" &&
                            field !== "tunjangan_jabatan" &&
                            field !== "lembur" &&
                            field !== "rajin")
                        }
                        type="text"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        value={String(formik.values[field] || 0)}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          const digits = value.replace(/\D/g, "");
                          const cleaned = digits.replace(/^0+/, "") || "0";
                          formik.setFieldValue(name, Number(cleaned));
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MoneyOutlined color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Potongan Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  mb={2}
                  fontWeight="bold"
                >
                  POTONGAN
                </Typography>
                <Grid container spacing={3}>
                  {[
                    "sisa_nbh_bln_ini",
                    "nbh_bln_ini",
                    "potongan_nbh",
                    "potongan_tabungan",
                    "potongan_pinjaman",
                  ].map((field) => (
                    <Grid item xs={12} md={6} key={field}>
                      <TextField
                        fullWidth
                        label={formatFieldLabel(field)}
                        name={field}
                        type="text"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        value={String(formik.values[field] || 0)}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          const digits = value.replace(/\D/g, "");
                          const cleaned = digits.replace(/^0+/, "") || "0";
                          formik.setFieldValue(name, Number(cleaned));
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalculateOutlined color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Summary Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  mb={2}
                  fontWeight="bold"
                >
                  RINGKASAN
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Sisa NBH"
                      name="sisa_nbh"
                      type="text"
                      value={String(hitungSisaNBH())}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkHistory color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Estimasi Total Tabungan"
                      name="total_tabungan"
                      type="text"
                      value={formatRupiah(hitungEstimasiTabungan())}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Savings color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Total Pendapatan"
                      name="total_income"
                      type="text"
                      value={formatRupiah(hitungIncome())}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyOutlined color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Total Potongan"
                      name="total_outcome"
                      type="text"
                      value={formatRupiah(hitungOutcome())}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalculateOutlined color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Total Gaji"
                      name="total_gaji"
                      type="text"
                      value={formatRupiah(hitungTotalGaji())}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Savings color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                          "& > fieldset": {
                            borderColor: theme.palette.secondary.main,
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Action Buttons */}
              <FlexStart gap={2} sx={{ flexWrap: "wrap" }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={hitungTotalGaji() === 0}
                  startIcon={<AddCircleOutline />}
                  sx={{
                    background: theme.palette.secondary.main,
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "12px 28px",
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: `0 4px 14px ${theme.palette.secondary.main}40`,
                    "&:hover": {
                      background: theme.palette.secondary[300],
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px ${theme.palette.secondary.main}50`,
                    },
                    "&:disabled": {
                      bgcolor: theme.palette.grey[300],
                      color: theme.palette.grey[500],
                    },
                    color: "#ffffff",
                    transition: "all 0.3s ease",
                  }}
                >
                  Simpan
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  startIcon={<RefreshOutlined />}
                  onClick={() => {
                    formik.resetForm();
                    setBaseTotalHadir(0);
                    dispatch(resetSummary());
                  }}
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "12px 28px",
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    "&:hover": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                      bgcolor: `${theme.palette.secondary.main}10`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  startIcon={<CancelOutlined />}
                  onClick={() => {
                    navigate(-1);
                    formik.resetForm();
                    dispatch(resetSummary());
                  }}
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "12px 28px",
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    "&:hover": {
                      borderColor: theme.palette.error.dark,
                      color: theme.palette.error.dark,
                      bgcolor: `${theme.palette.error.main}10`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Batal
                </Button>
              </FlexStart>
            </CardContent>
          </Card>
        )}
      </form>
      <FloatingAlert
        open={message.length > 0}
        onClose={handleCloseSnackbar}
        message={message}
        severity={status === "failed" ? "error" : status}
      />
    </Box>
  );
};

export default AddPenggajian;
